import { attachable, buildMail, daysAgo, deliveryLine, fmtSize, type Rfq, t } from '@procumi/shared';
import { useState } from 'react';
import { api, errMsg } from '../lib/api';
import { copyText } from '../lib/clipboard';
import { autoSendAllowed, companyName, currentUser } from '../lib/me';
import type { Toast } from '../hooks/useToast';
import { Dialog } from './Dialog';
import { swMail } from '../lib/terms';

const row = {
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
} as const;

export function MailDialog({
  rfq,
  signature,
  toast,
  sendId,
  onUpdated,
  onClose,
}: {
  rfq: Rfq;
  signature: string;
  toast: Toast;
  /** furnizorul deschis la început; altfel primul */
  sendId?: string;
  onUpdated: (updated: Rfq) => void;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(() =>
    Math.max(
      0,
      rfq.sends.findIndex((s) => s.id === sendId),
    ),
  );
  const [busy, setBusy] = useState(false);
  const send = rfq.sends[idx] ?? rfq.sends[0];
  const mail = swMail(
    buildMail(
      { ...rfq, site: deliveryLine(rfq.site), company: companyName() },
      send ? { name: send.supplier.name, contact: send.contact.name } : null,
      signature,
    ),
  );
  const unsent = rfq.sends.filter((s) => !s.sentAt).length;
  const auto = autoSendAllowed();
  const plans = rfq.files ?? [];

  /** Din Procumi: furnizorul ales sau, fără el, toți la care n-a plecat. */
  const mailIt = (sendIds?: string[]) =>
    run(
      async () => {
        const r = await api.mailRfq(rfq.id, sendIds);
        if (r.failed.length) {
          toast(t('N-a plecat la {0}', r.failed.map((f) => `${f.supplier} (${f.error})`).join(', ')));
        } else {
          toast(r.sent === 1 ? t('Mailul a plecat') : t('Au plecat {0} mailuri', r.sent));
        }
        return r.rfq;
      },
      (updated) => {
        const next = updated.sends.findIndex((s) => !s.sentAt);
        if (next >= 0) setIdx(next);
      },
    );

  const run = async (call: () => Promise<Rfq>, done: (updated: Rfq) => void) => {
    setBusy(true);
    try {
      const updated = await call();
      onUpdated(updated);
      done(updated);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  // După „Am trimis", trecem singuri la următorul furnizor netrimis: omul
  // trimite mailurile unul după altul, nu vrea să caute cine a rămas.
  const markSent = (sid: string, name: string) =>
    run(
      () => api.markSent(rfq.id, sid),
      (updated) => {
        toast(t('Marcat ca trimis: {0}', name));
        const next = updated.sends.findIndex((s) => !s.sentAt);
        if (next >= 0) setIdx(next);
      },
    );

  return (
    <Dialog onClose={onClose}>
      <div className="dlg-h">
        <div>
          <h2>{t('Mailurile de trimis')}</h2>
          {auto ? (
            <p className="hint">
              {t(
                'Fiecare dintre cei {0} furnizori primește un mail separat, cu o cerere de ofertă formală. Procumi îl trimite în numele tău; când furnizorul răspunde, mailul ajunge la {1}. Mailul din Procumi are și un buton prin care furnizorul trece oferta direct în tabel. Poți și să-l copiezi și să-l trimiți tu.',
                rfq.sends.length,
                currentUser().email,
              )}
            </p>
          ) : (
            <p className="hint">
              {t(
                'Fiecare dintre cei {0} furnizori primește un mail separat, cu același text. Alege furnizorul, deschide un mail nou în programul tău de mail și copiază pe rând adresa, subiectul și textul. După ce l-ai trimis, apasă „Am trimis”.',
                rfq.sends.length,
              )}
            </p>
          )}
        </div>
      </div>
      <div className="dlg-b">
        <div className="filters" style={{ margin: 0 }}>
          {rfq.sends.map((s, k) => (
            <button key={s.id} aria-pressed={k === idx} onClick={() => setIdx(k)}>
              {s.sentAt ? '✓ ' : ''}
              {s.supplier.name}
            </button>
          ))}
        </div>
        {send ? (
          <div style={row}>
            <span>
              <b>{t('Către:')}</b> {send.contact.name || send.supplier.name} · {send.contact.email}
            </span>
            <button
              className="btn small"
              disabled={!send.contact.email}
              onClick={() => void copyText(send.contact.email, toast)}
            >
              {t('Copiază adresa')}
            </button>
          </div>
        ) : null}
        <div style={row}>
          <span>
            <b>{t('Subiect:')}</b> {mail.subject}
          </span>
          <button className="btn small" onClick={() => void copyText(mail.subject, toast)}>
            {t('Copiază subiectul')}
          </button>
        </div>
        <div className="mail">{mail.body}</div>
        {plans.length ? (
          <div className="mail-plans">
            <b>{t('Planuri și documente ({0}).', plans.length)}</b>{' '}
            {auto
              ? attachable(plans)
                ? t('Pleacă atașate la mailul din Procumi. Dacă îl trimiți tu, atașează-le:')
                : t(
                    'Pleacă în mailul din Procumi ca linkuri de descărcare, fiindcă sunt mari. Dacă îl trimiți tu, atașează-le sau trimite-le separat:',
                  )
              : t('Atașează-le în mailul tău:')}
            <ul>
              {plans.map((f) => (
                <li key={f.id}>
                  <a href={api.rfqFileUrl(rfq.id, f.id)}>{f.filename}</a>{' '}
                  <span className="muted">· {fmtSize(f.size)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {send?.mailError ? (
          <p className="mail-error" role="alert">
            {t('Ultimul mail din Procumi n-a plecat: {0}', send.mailError)}
          </p>
        ) : null}
        <div style={row}>
          <button className="btn small" onClick={() => void copyText(mail.body, toast)}>
            {t('Copiază textul')}
          </button>
          {send ? (
            send.sentAt ? (
              <span style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="pill wait">
                  {send.sentVia === 'procumi' ? t('Trimis din Procumi') : t('Trimis')} {daysAgo(send.sentAt)}
                </span>
                {auto && !send.offer ? (
                  <button className="btn small" disabled={busy} onClick={() => void mailIt([send.id])}>
                    {t('Trimite din nou')}
                  </button>
                ) : null}
                <button
                  className="btn small"
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () => api.unmarkSent(rfq.id, send.id),
                      () => toast(t('{0}: marcat din nou ca netrimis', send.supplier.name)),
                    )
                  }
                >
                  {t('Nu l-am trimis')}
                </button>
              </span>
            ) : (
              <span style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className={auto ? 'btn small' : 'btn small primary'}
                  disabled={busy}
                  onClick={() => void markSent(send.id, send.supplier.name)}
                >
                  {t('Am trimis')}
                </button>
                {auto ? (
                  <button
                    className={unsent > 1 ? 'btn small' : 'btn small primary'}
                    disabled={busy || !send.contact.email}
                    onClick={() => void mailIt([send.id])}
                  >
                    {t('Trimite din Procumi')}
                  </button>
                ) : null}
              </span>
            )
          ) : null}
        </div>
      </div>
      <div className="dlg-f">
        {auto && unsent > 1 ? (
          <button className="btn primary" disabled={busy} onClick={() => void mailIt()}>
            {busy ? t('Se trimite…') : t('Trimite din Procumi la toți ({0})', unsent)}
          </button>
        ) : null}
        {unsent > 1 ? (
          <button
            className="btn"
            disabled={busy}
            onClick={() =>
              void run(
                () => api.markAllSent(rfq.id),
                () => toast(t('Marcate ca trimise: {0} mailuri', unsent)),
              )
            }
          >
            {t('Le-am trimis pe toate')}
          </button>
        ) : null}
        <button className={auto && unsent > 1 ? 'btn' : 'btn primary'} onClick={onClose}>
          {t('Închide')}
        </button>
      </div>
    </Dialog>
  );
}
